/**
 * Manager Access API — Phase D1.
 *
 * Artist-owner-scoped manager grants. Mirrors the org-mediated invitation
 * surface but goes through `/api/me/managers` so callers don't need to know
 * about organizations at all.
 */

import axiosInstance from '../utils/axiosInstance';

export interface DirectManager {
  _id: string;
  userId: string;
  domainSlug: string;
  assignedAt?: string;
  fullName: string;
  email: string;
  /** 'direct' = invited by the artist owner (revocable here).
   *  'organization' = granted via a management company (read-only here;
   *  the org admin manages it). */
  source?: 'direct' | 'organization';
  organization?: { _id: string; name: string; slug: string } | null;
}

export interface DirectInvitation {
  _id: string;
  email: string;
  invitedRole: string;
  targetDomainSlug: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expiresAt?: string;
  createdAt?: string;
}

export interface DirectInviteResult {
  /** True when the target email already had a registered account and access
   *  was granted directly without an invitation token. */
  granted: boolean;
  /** True when the user already had active access (idempotent re-grant). */
  alreadyHadAccess?: boolean;
  /** Populated on the granted path. */
  manager?: { userId: string; email: string; fullName: string; domainSlug: string };
  /** Populated on the legacy invitation-token path. */
  invitation?: DirectInvitation;
  /** Raw share-link token. Prefer this over {@link inviteUrl} when building
   *  the URL on the client so the link uses the same origin the user is on
   *  (e.g. localhost in dev). */
  token?: string;
  inviteUrl?: string;
}

export const managerAccessApi = {
  async list(): Promise<{ slug: string; managers: DirectManager[]; invitations: DirectInvitation[] }> {
    const { data } = await axiosInstance.get('/api/me/managers');
    return {
      slug: data?.slug || '',
      managers: data?.managers || [],
      invitations: data?.invitations || [],
    };
  },

  async invite(email: string): Promise<DirectInviteResult> {
    const { data } = await axiosInstance.post('/api/me/managers/invitations', { email });
    return {
      granted: !!data?.granted,
      alreadyHadAccess: !!data?.alreadyHadAccess,
      manager: data?.manager,
      invitation: data?.invitation,
      token: data?.token,
      inviteUrl: data?.inviteUrl,
    };
  },

  async revokeInvitation(id: string): Promise<void> {
    await axiosInstance.delete(`/api/me/managers/invitations/${id}`);
  },

  async revokeManager(userId: string): Promise<void> {
    await axiosInstance.delete(`/api/me/managers/${userId}`);
  },
};

/**
 * Super-admin variant — same operations but scoped to an explicit slug.
 * Uses /api/admin/domains/:slug/managers/* instead of /api/me/managers/*.
 */
export const adminManagerAccessApi = {
  async list(slug: string): Promise<{ slug: string; managers: DirectManager[]; invitations: DirectInvitation[] }> {
    const { data } = await axiosInstance.get(`/api/admin/domains/${slug}/managers`);
    return {
      slug: data?.slug || slug,
      managers: data?.managers || [],
      invitations: data?.invitations || [],
    };
  },

  async invite(slug: string, email: string): Promise<DirectInviteResult> {
    const { data } = await axiosInstance.post(`/api/admin/domains/${slug}/managers/invitations`, { email });
    return {
      granted: !!data?.granted,
      alreadyHadAccess: !!data?.alreadyHadAccess,
      manager: data?.manager,
      invitation: data?.invitation,
      token: data?.token,
      inviteUrl: data?.inviteUrl,
    };
  },

  async revokeInvitation(slug: string, id: string): Promise<void> {
    await axiosInstance.delete(`/api/admin/domains/${slug}/managers/invitations/${id}`);
  },

  async revokeManager(slug: string, userId: string): Promise<void> {
    await axiosInstance.delete(`/api/admin/domains/${slug}/managers/${userId}`);
  },
};
